/**
 * dionis-popup.js  v0.3.13-integration-candidate
 * Web Component for the Dionis signup popup (base + baby audiences).
 *
 * v0.3 integrates the hardened component into Blockshop 12.2.1:
 * deterministic display modes with all A/B machinery removed; Baby
 * list routing and welcome-trigger properties; Shopify privacy-aware
 * onsite analytics; OOS-safe Klaviyo queue handling; partial phone
 * failure detection; stronger NANP validation; and a terminal
 * success state that auto-closes about 3 seconds after SMS accept.
 *
 * v0.3.5 (2026-07-21): returning subscribers are never asked for an
 * email; the SMS request is phone-only when no session identity
 * exists and stitched when one does (supersedes COR-02). Honeypot is
 * demoted to a tracked signal after a field-verified autofill false
 * positive. SMS-only entry applies returning-subscriber copy. A
 * restrained terminal-success confetti burst, gated behind reduced
 * motion and design mode.
 *
 * v0.3.9 (2026-07-22): every class, id, and name token migrates
 * from the dp namespace to dnp after a third-party datepicker
 * stylesheet collided on the dp class names. The terminal
 * auto-close timer is tracked so disconnection clears it.
 *
 * v0.3.12 (2026-07-27, per Baruch): baby follows the base
 * suppression ladder. Email-accepted becomes sms-only with the
 * 15% return tab instead of full suppression, and the baby
 * session email persists for reentry stitching like base.
 *
 * v0.3.13 (2026-07-27): external-review pass. A reentry SMS no
 * longer rewrites first-capture timestamps and now stamps
 * baby_sms_accepted/_at; phone error clearing keys on real NANP
 * validity; theme-editor block select/deselect handled; the
 * privacy fail-open posture is documented as a recorded decision.
 */

(() => {
  'use strict';

  if (customElements.get('dionis-popup')) return;

  const FETCH_TIMEOUT_MS = 10000;
  const KLAVIYO_WAIT_MS = 4000;
  const SUBSCRIBE_TTL_DAYS = 365;
  const AUTO_SHOWN_TTL_DAYS = 1;
  const GENERIC_ERROR =
    'We could not complete your signup. Please check your information and try again.';

  /* ---------------- cookie + storage helpers ---------------- */

  const cookies = {
    set(name, value, days) {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      const secure = location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)};path=/;expires=${expires};SameSite=Lax${secure}`;
    },
    get(name) {
      const target = `${encodeURIComponent(name)}=`;
      for (const part of document.cookie.split('; ')) {
        if (part.startsWith(target)) {
          return decodeURIComponent(part.slice(target.length));
        }
      }
      return null;
    },
    remove(name) {
      const secure = location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${encodeURIComponent(name)}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;SameSite=Lax${secure}`;
    },
  };

  const session = {
    set(key, value) {
      try { sessionStorage.setItem(key, value); } catch { /* non-fatal */ }
    },
    get(key) {
      try { return sessionStorage.getItem(key); } catch { return null; }
    },
    remove(key) {
      try { sessionStorage.removeItem(key); } catch { /* non-fatal */ }
    },
  };


  /* ---------------- privacy + Klaviyo queue helpers ---------------- */

  /* Deliberate fail-open (recorded 2026-07-27): this store runs no
     consent banner (the consent-tracking block is disabled), so an
     absent or late Customer Privacy API means tracking is permitted
     and the popup matches the sitewide posture. If Dionis ever
     enables consent tracking, flip the absent-API branch to return
     false so the optional identify/track calls fail closed. */
  function privacyPermission(methodName) {
    const api = window.Shopify?.customerPrivacy;
    if (!api || typeof api[methodName] !== 'function') return true;
    try { return Boolean(api[methodName]()); } catch { return false; }
  }

  const analyticsAllowed = () => privacyPermission('analyticsProcessingAllowed');
  const marketingAllowed = () => privacyPermission('marketingAllowed');

  function hasUsableKlaviyoQueue() {
    if (window._klOnsite && typeof window._klOnsite.push === 'function') return true;
    return Boolean(
      window.klaviyo &&
      typeof window.klaviyo.push === 'function'
    );
  }

  function pushKlaviyo(command) {
    try {
      if (window._klOnsite && typeof window._klOnsite.push === 'function') {
        window._klOnsite.push(command);
        return true;
      }
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(command);
        return true;
      }
    } catch { /* non-fatal */ }
    return false;
  }

  /* ---------------- Klaviyo readiness (bounded, retryable) ------- */
  /* A timeout resolves null for THIS waiter but clears the cache so
     a later call can pick up a slow custom loader (P1-12). Final
     behavior pending loader review; see INTEGRATION.md item 2. */

  let klaviyoPromise = null;

  function klaviyoReady() {
    if (hasUsableKlaviyoQueue()) return Promise.resolve(true);
    if (klaviyoPromise) return klaviyoPromise;
    klaviyoPromise = new Promise((resolve) => {
      const finish = (retryable) => {
        clearTimeout(timer);
        window.removeEventListener('klaviyo:loaded', onLoaded);
        if (retryable) klaviyoPromise = null;
        resolve(hasUsableKlaviyoQueue());
      };
      const onLoaded = () => finish(false);
      const timer = setTimeout(() => finish(true), KLAVIYO_WAIT_MS);
      window.addEventListener('klaviyo:loaded', onLoaded);
    });
    return klaviyoPromise;
  }

  /* ---------------- validation helpers ---------------- */

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  function cleanPhone(raw) {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    return digits;
  }

  function isValidUsPhone(digits) {
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
  }

  function formatPhone(input) {
    const d = cleanPhone(input.value).slice(0, 10);
    let out = '';
    if (d.length > 6) out = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    else if (d.length > 3) out = `(${d.slice(0, 3)}) ${d.slice(3)}`;
    else if (d.length > 0) out = `(${d}`;
    input.value = out;
  }

  /* ---------------- component ---------------- */

  class DionisPopup extends HTMLElement {
    static activeInstance = null;

    #config = null;
    #refs = {};
    #ac = null;
    #fetchAc = null;
    #timers = new Set();
    #headingFocusTimer = null;
    #invoker = null;
    #email = '';
    #openedAt = 0;
    #stepStartedAt = 0;
    #mode = 'full';
    #babyCapturedAt = '';
    #impressionSent = false;
    #smsAccepted = false;
    #celebrated = false;
    #designMode = Boolean(window.Shopify && window.Shopify.designMode);
    #initialized = false;

    connectedCallback() {
      if (this.#initialized) return;

      // Round 2 P1-01: validate config BEFORE claiming the active
      // slot, so a misconfigured instance can never squat on it.
      const config = this.#readConfig();
      if (!config) {
        this.#standDown('invalid');
        return;
      }

      // Defensive single-instance guard: the first valid, connected
      // instance wins. Duplicates are hidden and stay inert for the
      // rest of the page life. No automatic promotion by design:
      // canonical one-instance placement is the real protection and
      // this is a net, not a multi-instance manager.
      const active = DionisPopup.activeInstance;
      if (active && active !== this && active.isConnected) {
        this.#standDown('duplicate');
        console.warn('[dionis-popup] duplicate instance ignored; one popup per page.');
        return;
      }

      this.#config = config;
      this.#collectRefs();
      if (!this.#validateRefs()) {
        this.#config = null;
        this.#standDown('invalid');
        return;
      }

      this.hidden = false;
      DionisPopup.activeInstance = this;
      this.#initialized = true;

      this.#ac = new AbortController();
      this.#bindListeners();
      this.#resolveEligibility();
    }

    // Round 3 P2-01: an incomplete component stands down rather than
    // partially operating (e.g. collecting email then failing SMS).
    #validateRefs() {
      const r = this.#refs;
      return Boolean(r.trigger && r.open && r.dialog && r.stepEmail &&
        r.stepSms && r.headingEmail && r.headingSms && r.formEmail &&
        r.formSms && r.emailInput && r.emailError && r.phoneInput &&
        r.phoneError && r.consent && r.consentError && r.smsStatus &&
        r.skip);
    }

    // Force a non-active instance fully inert. Requires the
    // dionis-popup[hidden] CSS rule: the component's display:contents
    // would otherwise override the hidden attribute's UA display:none.
    #standDown(reason) {
      this.dataset.eligibility = reason;
      this.hidden = true;
      const trigger = this.querySelector('[data-ref="trigger"]');
      if (trigger) trigger.hidden = true;
      const dialog = this.querySelector('dialog');
      if (dialog?.open) dialog.close();
    }

    disconnectedCallback() {
      if (DionisPopup.activeInstance === this) DionisPopup.activeInstance = null;
      this.#ac?.abort();
      this.#fetchAc?.abort();
      for (const id of this.#timers) clearTimeout(id);
      this.#timers.clear();
      this.#headingFocusTimer = null;
      if (this.#refs.dialog?.open) this.#refs.dialog.close();
      this.#initialized = false;
    }

    /* ---------- setup ---------- */

    #readConfig() {
      const el = this.querySelector('script[data-popup-config]');
      if (!el) return null;
      try {
        const cfg = JSON.parse(el.textContent);
        const required = ['audienceStateKey', 'displayStateKey', 'companyId',
          'emailListId', 'smsListId', 'revision', 'audience'];
        if (required.some((key) => !cfg[key])) return null;
        if (cfg.audience !== 'base' && cfg.audience !== 'baby') return null;
        if (cfg.displayMode !== 'fab' && cfg.displayMode !== 'auto') return null;
        return cfg;
      } catch {
        return null;
      }
    }

    #collectRefs() {
      const q = (name) => this.querySelector(`[data-ref="${name}"]`);
      this.#refs = {
        trigger: q('trigger'),
        open: q('open'),
        dismiss: q('dismiss'),
        dialog: q('dialog'),
        stepEmail: q('step-email'),
        stepSms: q('step-sms'),
        headingEmail: q('heading-email'),
        headingSms: q('heading-sms'),
        formEmail: q('form-email'),
        formSms: q('form-sms'),
        emailInput: q('email-input'),
        emailError: q('email-error'),
        choices: q('choices'),
        choicesError: q('choices-error'),
        phoneInput: q('phone-input'),
        phoneError: q('phone-error'),
        subheadingSms: q('subheading-sms'),
        offerSms: q('offer-sms'),
        descriptionSms: q('description-sms'),
        consent: q('consent'),
        consentError: q('consent-error'),
        smsStatus: q('sms-status'),
        skip: q('skip'),
        hpEmail: q('hp-email'),
        hpSms: q('hp-sms'),
      };
    }

    #bindListeners() {
      const s = { signal: this.#ac.signal };
      const r = this.#refs;

      r.open?.addEventListener('click', () => this.#open(r.open), s);
      r.dismiss?.addEventListener('click', () => this.#dismissForTtl(), s);
      r.formEmail?.addEventListener('submit', (e) => this.#onEmailSubmit(e), s);
      r.formSms?.addEventListener('submit', (e) => this.#onSmsSubmit(e), s);
      r.skip?.addEventListener('click', () => this.#onSkip(), s);
      r.emailInput?.addEventListener('input', () => {
        if (isEmail(r.emailInput.value.trim())) {
          this.#setFieldError(r.emailInput, r.emailError, '');
        }
      }, s);
      r.phoneInput?.addEventListener('input', () => {
        formatPhone(r.phoneInput);
        if (isValidUsPhone(cleanPhone(r.phoneInput.value))) {
          this.#setFieldError(r.phoneInput, r.phoneError, '');
        }
      }, s);

      // P1-05: selecting a radio clears the group's invalid state.
      r.choices?.addEventListener('change', () => this.#setChoicesInvalid(false), s);
      r.consent?.addEventListener('change', () => {
        if (r.consent.checked) this.#setConsentInvalid(false);
      }, s);

      for (const btn of this.querySelectorAll('[data-ref="close"]')) {
        btn.addEventListener('click', () => r.dialog?.close(), s);
      }

      r.dialog?.addEventListener('close', () => this.#onDialogClose(), s);
      r.dialog?.addEventListener('click', (e) => {
        if (e.target === r.dialog) r.dialog.close();
      }, s);

      if (this.#designMode && this.#config.sectionId) {
        document.addEventListener('shopify:section:select', (e) => {
          if (e.detail?.sectionId === this.#config.sectionId) this.#open(null, true);
        }, s);
        document.addEventListener('shopify:section:deselect', (e) => {
          if (e.detail?.sectionId === this.#config.sectionId) r.dialog?.close();
        }, s);
        // v0.3.13: block-level editor events. legacyBlockId carries
        // block.id, so it doubles as the block selector key.
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail?.blockId === this.#config.legacyBlockId) this.#open(null, true);
        }, s);
        document.addEventListener('shopify:block:deselect', (e) => {
          if (e.detail?.blockId === this.#config.legacyBlockId) r.dialog?.close();
        }, s);
      }
    }

    /* ---------- eligibility (synchronous, cookie-only) ---------- */

    // Round 3 P1-03: three key scopes with different lifecycles.
    // Audience keys are UNVERSIONED durable channel state; display
    // keys carry the offer version and short-lived presentation state.
    #aKey(kind) {
      return `${this.#config.audienceStateKey}--${kind}`;
    }

    #dKey(kind) {
      return `${this.#config.displayStateKey}--${kind}`;
    }


    // Legacy production cookies (KlaviyoEmailSubscribed-{blockId} etc)
    // are read once and migrated forward to the durable audience keys
    // so existing subscribed browsers are not reset at rollout, then
    // ALL legacy cookies are deleted, including the raw-email
    // KlaviyoUserEmail PII cookie and the retired A/B cookies.
    // Migration is active for the reused production block because
    // legacyBlockId serializes block.id; a fresh-state rollout would
    // require blanking legacyBlockId in the snippet. Ratified behavior: migrate email and SMS completion, purge raw email and retired experiment state, and do not invent a fresh close TTL.
    #migrateLegacyState() {
      const id = this.#config.legacyBlockId;
      if (!id || this.#config.audience !== 'base') return;
      if (!cookies.get(this.#aKey('email-accepted')) &&
          cookies.get(`KlaviyoEmailSubscribed-${id}`)) {
        cookies.set(this.#aKey('email-accepted'), '1', SUBSCRIBE_TTL_DAYS);
      }
      if (!cookies.get(this.#aKey('sms-accepted')) &&
          cookies.get(`KlaviyoSMSSubscribed-${id}`)) {
        cookies.set(this.#aKey('sms-accepted'), '1', SUBSCRIBE_TTL_DAYS);
      }
      for (const legacy of [
        `KlaviyoUserEmail-${id}`,
        `KlaviyoABVariant-${id}`,
        `KlaviyoAutoPopupShown-${id}`,
        `KlaviyoPopupClosed-${id}`,
        `KlaviyoEmailSubscribed-${id}`,
        `KlaviyoSMSSubscribed-${id}`,
      ]) {
        cookies.remove(legacy);
      }
    }

    #resolveEligibility() {
      this.#migrateLegacyState();

      const closed = cookies.get(this.#dKey('closed'));
      const email = cookies.get(this.#aKey('email-accepted'));
      const sms = cookies.get(this.#aKey('sms-accepted'));

      if (closed) return this.#commit('suppressed');
      // v0.3.12: baby uses the same ladder as base; email-accepted
      // yields sms-only (15% tab), never immediate suppression.
      // Round 3 P2-02 (read side): SMS acceptance implied a completed
      // email association, so an SMS cookie alone suppresses even if
      // the email cookie has expired first.
      if (sms) return this.#commit('suppressed');
      if (email) return this.#commit('sms-only');
      return this.#commit('full');
    }

    #commit(mode) {
      this.#mode = mode;
      this.dataset.eligibility = mode === 'suppressed' ? 'suppressed' : 'eligible';
      if (mode === 'suppressed') {
        const focusWasInTrigger =
          Boolean(this.#refs.trigger?.contains(document.activeElement));
        this.#hideTrigger();
        if (focusWasInTrigger) this.#focusPageFallback();
        return;
      }

      if (mode === 'sms-only') {
        this.#setTriggerLabel(this.#config.triggerLabelSms || this.#config.triggerLabel);
        this.#showTrigger();
        return;
      }

      this.#setTriggerLabel(this.#config.triggerLabel);
      const useAuto =
        this.#config.audience === 'base' &&
        this.#config.displayMode === 'auto' &&
        !this.#designMode;

      if (useAuto) this.#scheduleAuto();
      else this.#showTrigger();
    }

    #scheduleAuto() {
      if (cookies.get(this.#dKey('auto-shown'))) {
        this.#showTrigger();
        return;
      }
      const delay = Number(this.#config.autoDelayMs) || 5000;
      const id = setTimeout(() => {
        this.#timers.delete(id);
        if (!this.isConnected || this.#mode === 'suppressed') return;
        cookies.set(this.#dKey('auto-shown'), '1', AUTO_SHOWN_TTL_DAYS);
        this.#open(null);
      }, delay);
      this.#timers.add(id);
    }

    #setTriggerLabel(text) {
      const label = this.#refs.open?.querySelector('[data-ref="open-label"]');
      if (label && text) label.textContent = text;
    }

    #showTrigger() {
      if (this.#refs.trigger) this.#refs.trigger.hidden = false;
      // P2-01: impression means first eligible display per pageview.
      if (!this.#impressionSent) {
        this.#impressionSent = true;
        this.#track('Popup Impression', {});
      }
    }

    #hideTrigger() {
      if (this.#refs.trigger) this.#refs.trigger.hidden = true;
    }

    /* ---------- open / close / steps ---------- */

    #open(invoker, isPreview = false) {
      const r = this.#refs;
      if (!r.dialog || r.dialog.open) return;

      this.#resetTransientUi();
      this.#invoker = invoker || null;
      this.#openedAt = Date.now();
      if (invoker) invoker.setAttribute('aria-expanded', 'true');
      this.#hideTrigger();

      const startOnSms = this.#mode === 'sms-only';
      this.#activateStep(startOnSms ? 'sms' : 'email');

      if (startOnSms) {
        const stored = this.#email || session.get(this.#aKey('session-email')) || '';
        if (stored) this.#email = stored;
        this.#applyReentryCopy();
      }

      this.#celebrated = false;
      r.dialog.showModal();
      if (!isPreview && !this.#impressionSent) {
        // auto-open counts as the pageview's impression too
        this.#impressionSent = true;
        this.#track('Popup Impression', {});
      }
      if (!isPreview) {
        this.#track('Popup Opened', { step: startOnSms ? 'sms' : 'email' });
      }
      this.#focusHeading(startOnSms ? 'sms' : 'email');
    }

    // P1-06: required semantics follow visibility.
    #resetTransientUi() {
      const r = this.#refs;
      this.#setFieldError(r.emailInput, r.emailError, '');
      this.#setFieldError(r.phoneInput, r.phoneError, '');
      this.#setChoicesInvalid(false);
      this.#setConsentInvalid(false);
      if (r.smsStatus) {
        r.smsStatus.textContent = '';
        r.smsStatus.hidden = true;
      }
    }

    // Returning-subscriber framing (decision 2026-07-21): SMS-only
    // entry shows offer copy, never same-session celebration copy,
    // and never asks for an email.
    #applyReentryCopy() {
      const c = this.#config;
      const r = this.#refs;
      if (r.headingSms && c.reentryHeadingSms) {
        r.headingSms.textContent = c.reentryHeadingSms;
      }
      if (r.subheadingSms && c.reentrySubheadingSms) {
        r.subheadingSms.textContent = c.reentrySubheadingSms;
      }
      if (r.offerSms && c.reentryOfferSms) {
        r.offerSms.textContent = c.reentryOfferSms;
      }
      if (r.descriptionSms) r.descriptionSms.hidden = true;
    }

    #activateStep(which) {
      const r = this.#refs;
      const emailActive = which === 'email';
      this.#stepStartedAt = Date.now();
      if (r.dialog) r.dialog.dataset.step = which;
      if (r.stepEmail) r.stepEmail.hidden = !emailActive;
      if (r.stepSms) r.stepSms.hidden = emailActive;
      const heading = emailActive ? r.headingEmail : r.headingSms;
      if (heading?.id && r.dialog) {
        r.dialog.setAttribute('aria-labelledby', heading.id);
      }
    }

    #focusHeading(which) {
      this.#cancelHeadingFocus();
      const heading = which === 'email' ? this.#refs.headingEmail : this.#refs.headingSms;
      const id = setTimeout(() => {
        this.#timers.delete(id);
        if (this.#headingFocusTimer === id) this.#headingFocusTimer = null;
        if (this.#refs.dialog?.open) heading?.focus();
      }, 50);
      this.#timers.add(id);
      this.#headingFocusTimer = id;
    }

    #cancelHeadingFocus() {
      if (this.#headingFocusTimer) {
        clearTimeout(this.#headingFocusTimer);
        this.#timers.delete(this.#headingFocusTimer);
        this.#headingFocusTimer = null;
      }
    }

    // P0-01: resolve next state FIRST so the trigger is visible
    // before we try to return focus to it; fall back deliberately
    // when the trigger stays suppressed (e.g. baby success).
    #onDialogClose() {
      this.#cancelHeadingFocus();

      const invoker = this.#invoker;
      this.#invoker = null;
      if (invoker) invoker.setAttribute('aria-expanded', 'false');

      this.#resolveEligibility();

      if (invoker && !invoker.closest('[hidden]')) {
        requestAnimationFrame(() => {
          if (!invoker.closest('[hidden]')) invoker.focus();
          else this.#focusPageFallback();
        });
        return;
      }
      this.#focusPageFallback();
    }

    #focusPageFallback() {
      const target =
        document.getElementById('main-content') || document.querySelector('main');
      if (!target) return;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }

    #dismissForTtl() {
      // Round 2 P1-02: hiding the trigger while focus sits on the
      // dismiss button would strand focus in a hidden subtree.
      const restoreFocus =
        Boolean(this.#refs.trigger?.contains(document.activeElement));

      cookies.set(this.#dKey('closed'), '1', Number(this.#config.closeTtlDays) || 3);
      this.#hideTrigger();
      this.#track('Popup Dismissed', { method: 'trigger_dismiss' });
      this.#mode = 'suppressed';
      this.dataset.eligibility = 'suppressed';

      if (restoreFocus) this.#focusPageFallback();
    }

    #onSkip() {
      cookies.set(this.#dKey('closed'), '1', Number(this.#config.closeTtlDays) || 3);
      this.#track('Popup Dismissed', { method: 'skip_sms' });
      this.#refs.dialog?.close();
      this.#mode = 'suppressed';
      this.dataset.eligibility = 'suppressed';
      session.remove(this.#aKey('session-email'));
    }

    /* ---------- validation UI ---------- */

    #setFieldError(input, errorEl, message) {
      if (errorEl) {
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
      }
      if (input) {
        input.setAttribute('aria-invalid', message ? 'true' : 'false');
        // P1-11: reflect invalid state on the composite phone wrapper.
        const wrapper = input.closest('.dnp-phone');
        wrapper?.classList.toggle('dnp-phone--invalid', Boolean(message));
      }
    }

    // P1-05: group-level invalid semantics for the baby radios.
    #setChoicesInvalid(invalid, message) {
      const r = this.#refs;
      if (!r.choices) return;
      for (const radio of r.choices.querySelectorAll('input[type="radio"]')) {
        radio.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      }
      if (r.choicesError) {
        if (invalid) {
          r.choicesError.textContent = message || 'Please choose one option.';
          r.choicesError.hidden = false;
        } else {
          r.choicesError.hidden = true;
        }
      }
    }

    #setConsentInvalid(invalid, message) {
      const r = this.#refs;
      if (r.consent) {
        r.consent.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      }
      if (r.consentError) {
        if (invalid) {
          r.consentError.textContent = message || 'Please agree to receive text messages.';
          r.consentError.hidden = false;
        } else {
          r.consentError.hidden = true;
        }
      }
    }

    /* ---------- submissions ---------- */

    // P1-04: honeypot only. Elapsed time is recorded as a signal,
    // never used to reject a valid human submission.
    #honeypotTripped(hpInput) {
      return Boolean(hpInput && hpInput.value.trim() !== '');
    }

    async #onEmailSubmit(event) {
      event.preventDefault();
      const r = this.#refs;
      const button = r.formEmail.querySelector('button[type="submit"]');
      if (button?.disabled) return;

      const email = (r.emailInput?.value || '').trim();
      let valid = true;

      if (!isEmail(email)) {
        this.#setFieldError(r.emailInput, r.emailError, 'Please enter a valid email address.');
        valid = false;
      } else {
        this.#setFieldError(r.emailInput, r.emailError, '');
      }

      let shopperContext = null;
      if (this.#config.audience === 'baby' && r.choices) {
        const checked = r.choices.querySelector('input[type="radio"]:checked');
        shopperContext = checked?.value || null;
        if (!shopperContext) {
          this.#setChoicesInvalid(true);
          valid = false;
        } else {
          this.#setChoicesInvalid(false);
        }
      }

      if (!valid) {
        const emailOk = isEmail(email);
        const firstInvalid = !emailOk
          ? r.emailInput
          : r.choices?.querySelector('input[type="radio"]');
        firstInvalid?.focus();
        return;
      }

      // 2026-07-21: honeypot is a signal, never a rejection. Browser
      // autofill writes into offscreen inputs (field verified in QA),
      // so a filled trap is not proof of a bot.
      if (this.#honeypotTripped(r.hpEmail)) {
        this.#track('Popup Honeypot Signal', { step: 'email' });
      }

      if (this.#designMode && !this.#config.testMode) {
        this.#setFieldError(null, r.emailError,
          'Submissions are disabled in the theme editor. Enable test mode to try it.');
        return;
      }

      const submittedAt = Date.now();
      const interactionMs = Math.max(0, submittedAt - (this.#stepStartedAt || this.#openedAt));
      const requestStartedAt = performance.now();

      this.#setLoading(button, true);
      try {
        await this.#subscribe({
          email,
          channel: 'email',
          shopperContext,
          customSource: this.#config.customSourceEmail,
          listId: this.#config.emailListId,
        });

        this.#email = email;
        session.set(this.#aKey('session-email'), email);
        cookies.set(this.#aKey('email-accepted'), '1', SUBSCRIBE_TTL_DAYS);

        // P1-03: identify FIRST so the accepted event is attributable;
        // both ride the same queue, so push order is preserved.
        this.#identify(email);
        this.#track('Popup Email Accepted', {
          shopper_context: shopperContext || undefined,
          interaction_ms: interactionMs,
          api_ms: Math.round(performance.now() - requestStartedAt),
        });

        // The shopper may close the dialog while fetch is pending.
        // Reconcile from durable state instead of mutating hidden steps.
        if (!r.dialog?.open) {
          this.#resolveEligibility();
          return;
        }

        this.#activateStep('sms');
        this.#focusHeading('sms');
      } catch (err) {
        if (this.#config.audience === 'baby') this.#babyCapturedAt = '';
        this.#track('Popup Error', {
          step: 'email',
          kind: err?.name || 'error',
          interaction_ms: interactionMs,
          api_ms: Math.round(performance.now() - requestStartedAt),
        });
        this.#setFieldError(r.emailInput, r.emailError, GENERIC_ERROR);
      } finally {
        this.#setLoading(button, false);
      }
    }

    async #onSmsSubmit(event) {
      event.preventDefault();
      const r = this.#refs;
      if (this.#smsAccepted) return;
      const button = r.formSms.querySelector('button[type="submit"]');
      if (button?.disabled) return;

      let valid = true;

      // Decision 2026-07-21: returning subscribers are never asked for
      // an email. Stitch when the session knows it, phone-only when not.
      const email = (this.#email || session.get(this.#aKey('session-email')) || '').trim();
      const hasEmail = isEmail(email);
      if (hasEmail) this.#email = email;

      const digits = cleanPhone(r.phoneInput?.value || '');
      if (!isValidUsPhone(digits)) {
        this.#setFieldError(r.phoneInput, r.phoneError, 'Please enter a valid US phone number.');
        valid = false;
      } else {
        this.#setFieldError(r.phoneInput, r.phoneError, '');
      }

      const consented = Boolean(r.consent?.checked);
      if (!consented) {
        this.#setConsentInvalid(true);
        valid = false;
      } else {
        this.#setConsentInvalid(false);
      }

      if (!valid) {
        const firstInvalid =
          (!isValidUsPhone(digits) && r.phoneInput) ||
          r.consent;
        firstInvalid?.focus();
        return;
      }

      if (this.#honeypotTripped(r.hpSms)) {
        this.#track('Popup Honeypot Signal', { step: 'sms' });
      }

      if (this.#designMode && !this.#config.testMode) {
        this.#setFieldError(null, r.phoneError,
          'Submissions are disabled in the theme editor. Enable test mode to try it.');
        return;
      }

      const submittedAt = Date.now();
      const interactionMs = Math.max(0, submittedAt - (this.#stepStartedAt || this.#openedAt));
      const requestStartedAt = performance.now();

      this.#setLoading(button, true);
      let accepted = false;
      try {
        await this.#subscribe({
          email: hasEmail ? email : null,
          phone: `+1${digits}`,
          channel: 'sms',
          shopperContext: this.#storedShopperContext(),
          customSource: this.#config.customSourceSms,
          listId: this.#config.smsListId,
        });

        accepted = true;
        this.#smsAccepted = true;
        // Round 3 P2-02 (write side): refresh both durable cookies
        // together so their expiry windows never diverge.
        cookies.set(this.#aKey('email-accepted'), '1', SUBSCRIBE_TTL_DAYS);
        cookies.set(this.#aKey('sms-accepted'), '1', SUBSCRIBE_TTL_DAYS);
        session.remove(this.#aKey('session-email'));
        // Round 2 P1-03: identify in THIS conversion path too, queued
        // before the accepted event, so attribution holds in sms-only
        // sessions where the email step never ran this page. Skipped
        // when no session identity exists (phone-only path).
        if (hasEmail) this.#identify(email);
        this.#track('Popup SMS Accepted', {
          interaction_ms: interactionMs,
          api_ms: Math.round(performance.now() - requestStartedAt),
        });

        this.#setLoading(button, false);

        // The shopper may close the dialog while fetch is pending.
        // Reconcile the visible trigger from the accepted channel state.
        if (!r.dialog?.open) {
          this.#resolveEligibility();
          return;
        }

        // P1-02: lock the form; success is terminal for this dialog.
        button.disabled = true;
        this.#lockSmsForm();
        if (r.smsStatus) {
          r.smsStatus.textContent = this.#config.smsSuccessMessage ||
            'Perfect! Check your phone for your discount code.';
          r.smsStatus.hidden = false;
          requestAnimationFrame(() => {
            if (r.dialog?.open) r.smsStatus.focus({ preventScroll: true });
          });
        }
        this.#celebrate();
        // 2026-07-21: terminal hold retired per Baruch. Close after
        // the burst finishes; suppression then owns the state.
        // v0.3.9: the close timer joins the tracked set so a
        // disconnect clears it like every other timer here.
        const closeId = window.setTimeout(() => {
          this.#timers.delete(closeId);
          if (this.#refs.dialog?.open) this.#refs.dialog.close();
        }, 3000);
        this.#timers.add(closeId);
      } catch (err) {
        this.#track('Popup Error', {
          step: 'sms',
          kind: err?.name || 'error',
          interaction_ms: interactionMs,
          api_ms: Math.round(performance.now() - requestStartedAt),
        });
        this.#setFieldError(r.phoneInput, r.phoneError, GENERIC_ERROR);
      } finally {
        if (!accepted) this.#setLoading(button, false);
      }
    }

    #lockSmsForm() {
      const r = this.#refs;
      for (const el of [r.phoneInput, r.consent, r.skip]) {
        if (el) el.disabled = true;
      }
    }

    #storedShopperContext() {
      const checked = this.#refs.choices?.querySelector('input[type="radio"]:checked');
      return checked?.value || null;
    }

    #setLoading(button, loading) {
      if (!button) return;
      button.disabled = loading;
      button.dataset.loading = String(loading);
    }

    /* ---------- Klaviyo ---------- */

    // P1-08: capture time is generated once per popup session, in
    // the email step only. v0.3.13: a reentry SMS (no email step in
    // this instance) must not rewrite first-capture timestamps; it
    // stamps SMS acceptance markers instead.
    #babyProperties(shopperContext, channel) {
      const props = {
        baby_interest: true,
        baby_shopper_context: shopperContext || undefined,
        baby_interest_source: 'baby_popup',
        signup_surface: 'baby_popup',
        signup_offer_version: this.#config.offerVersion || 'baby_launch_v1',
      };
      if (channel === 'email') {
        this.#babyCapturedAt ||= new Date().toISOString();
      }
      if (this.#babyCapturedAt) {
        props.baby_interest_captured_at = this.#babyCapturedAt;
        props.baby_popup_completed = true;
        props.baby_popup_completed_at = this.#babyCapturedAt;
      }
      if (channel === 'sms') {
        props.baby_sms_accepted = true;
        props.baby_sms_accepted_at = new Date().toISOString();
      }
      return props;
    }

    async #subscribe({ email, phone, channel, shopperContext, customSource, listId }) {
      // COR-02 superseded 2026-07-21: SMS may be phone-only when no
      // session identity exists. When an email is known it rides along
      // so the phone lands on the same profile.
      const attributes = {};
      if (email) attributes.email = email;
      if (phone) attributes.phone_number = phone;

      if (this.#config.audience === 'baby') {
        attributes.properties = this.#babyProperties(shopperContext, channel);
      } else {
        attributes.properties = {
          signup_surface: 'base_popup',
          signup_offer_version: this.#config.offerVersion || 'base_10_15_v2',
        };
      }

      attributes.subscriptions = channel === 'sms'
        ? { sms: { marketing: { consent: 'SUBSCRIBED' } } }
        : { email: { marketing: { consent: 'SUBSCRIBED' } } };

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes } },
            custom_source: customSource,
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      };

      this.#fetchAc?.abort();
      this.#fetchAc = new AbortController();
      const timeoutId = setTimeout(() => this.#fetchAc.abort(), FETCH_TIMEOUT_MS);
      this.#timers.add(timeoutId);

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.#config.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: this.#config.revision,
            },
            body: JSON.stringify(payload),
            signal: this.#fetchAc.signal,
          }
        );
        if (!response.ok && response.status !== 202) {
          throw new Error(`HTTP ${response.status}`);
        }

        const partialFailure =
          response.headers.get('X-Klaviyo-Partial-Failure') || '';
        const failedFields = partialFailure
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        if (failedFields.includes('phone_number')) {
          const error = new Error('Klaviyo rejected the phone number');
          error.name = 'PartialPhoneFailure';
          throw error;
        }

        return true;
      } finally {
        clearTimeout(timeoutId);
        this.#timers.delete(timeoutId);
      }
    }

    /* ---------- terminal-success confetti ---------- */

    // Restrained brand-palette burst on the terminal SMS success.
    // Hard-gated: reduced motion, design mode, one burst per open.
    #celebrate() {
      if (this.#celebrated || this.#designMode) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const step = this.#refs.stepSms;
      if (!this.#refs.dialog?.open || !step) return;
      this.#celebrated = true;

      const canvas = document.createElement('canvas');
      canvas.className = 'dnp-confetti';
      canvas.setAttribute('aria-hidden', 'true');
      const rect = step.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      const context = canvas.getContext('2d');
      if (!context) return;
      step.appendChild(canvas);

      const palette = this.#config.audience === 'baby'
        ? ['#cedfd7', '#4f6b5e', '#eef4f0', '#a9c6b8', '#ffffff']
        : ['#b88b76', '#8a5f4d', '#f0eadc', '#f0eadc', '#f0eadc', '#d9b795', '#ffffff', '#6f4a3b'];
      const pieces = Array.from({ length: 150 }, (unused, index) => ({
        x: canvas.width * (0.5 + (Math.random() - 0.5) * 0.9),
        y: canvas.height * (0.18 + Math.random() * 0.08),
        vx: (Math.random() - 0.5) * 7 * dpr,
        vy: (-4.5 + Math.random() * 2.5) * dpr,
        size: (5 + Math.random() * 6) * dpr,
        round: index % 3 === 0,
        tilt: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.35,
        sway: Math.random() * Math.PI * 2,
        color: palette[Math.floor(Math.random() * palette.length)],
      }));

      const started = performance.now();
      const DURATION_MS = 1900;
      const tick = (now) => {
        const elapsed = now - started;
        if (elapsed > DURATION_MS || !canvas.isConnected) {
          canvas.remove();
          return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.globalAlpha =
          elapsed > DURATION_MS - 350 ? (DURATION_MS - elapsed) / 350 : 1;
        for (const piece of pieces) {
          piece.sway += 0.08;
          piece.x += piece.vx + Math.sin(piece.sway) * 0.6 * dpr;
          piece.y += piece.vy;
          piece.vy += 0.14 * dpr;
          piece.vx *= 0.985;
          piece.tilt += piece.spin;
          context.save();
          context.translate(piece.x, piece.y);
          context.rotate(piece.tilt);
          context.fillStyle = piece.color;
        if (piece.round) {
            context.beginPath();
            context.arc(0, 0, piece.size / 2.6, 0, Math.PI * 2);
            context.fill();
          } else {
            context.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2);
         }
         context.restore();
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    // Queued identify and tracking are optional onsite enhancements.
    // The explicit subscription request remains available after form submit.
    #identify(email) {
      if (!marketingAllowed()) return;
      klaviyoReady().then((ready) => {
        if (!ready) return;
        pushKlaviyo(['identify', { email }]);
      });
    }

    #track(eventName, props) {
      if (this.#designMode || !analyticsAllowed()) return;
      const clean = { ...props };
      delete clean.email;
      delete clean.phone;
      klaviyoReady().then((ready) => {
        if (!ready) return;
        pushKlaviyo(['track', eventName, {
          ...clean,
          audience: this.#config.audience,
          audience_key: this.#config.audienceStateKey,
          display_key: this.#config.displayStateKey,
          offer_version: this.#config.offerVersion,
        }]);
      });
    }
  }

  customElements.define('dionis-popup', DionisPopup);
})();